import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAppSelector } from "../../../../hooks/store-hooks"
import { useLimitacaoConsumoService } from "../../../../../use-cases/limitacao-consumo"
import { useUIService } from "../../../../../use-cases/ui"
import { useNavigationService } from "../../../../../use-cases/navigation"
import { useProdutoLimitacaoConsumoApi } from "../../../../../infra/produto-limitacao-consumo"
import {
    AppBar,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Toolbar,
    Typography
} from "@mui/material"
import { onlyNumbers } from "../../../../../utils"
import { ILimitacaoConsumoTable, IProdutoLimitacaoConsumoDetails, IProdutoTipoConsumoLimitacaoConsumoAtivo } from "../../../../../application/models"
import CloseIcon from '@mui/icons-material/Close'
import CategoryIcon from '@mui/icons-material/Category'
import ChecklistIcon from '@mui/icons-material/Checklist'
import LayersIcon from '@mui/icons-material/Layers'

export const FormCreateTravaConsumoCliente = () => {
    const { createLimitacaoConsumoClienteForm, produtosList } = useAppSelector((state) => state.limitacaoConsumo)
    const { currentUser } = useAppSelector((state) => state.user)
    const [loading, setLoading] = useState<boolean>(true)
    const [travasAtivas, setTravasAtivas] = useState<ILimitacaoConsumoTable[]>([])
    const [idProdutoParaTiposConsumo, setIdProdutoParaTiposConsumo] = useState(0)
    const [tiposDoProdutoSelecionado, setTiposDoProdutoSelecionado] = useState<IProdutoTipoConsumoLimitacaoConsumoAtivo[]>([])
    const [loadingTipos, setLoadingTipos] = useState(false)
    const [searchTermTipos, setSearchTermTipos] = useState("")
    const isFirstRender = useRef(false)

    const {
        setCreateLimitacaoConsumoClienteOpenForm,
        setCreateLimitacaoConsumoClienteFormIdProduto,
        setCreateLimitacaoConsumoClienteFormModoTravaCliente,
        setCreateLimitacaoConsumoClienteFormIdTipoConsumo,
        setCreateLimitacaoConsumoClienteFormIdUsuario,
        setCreateLimitacaoConsumoClienteFormQuantidade,
        setCreateLimitacaoConsumoClienteFormIsGrupo,
        createLimitacaoConsumoCliente,
        getProdutoLimitacaoConsumoList,
        findAllActiveLimitacaoConsumoCliente
    } = useLimitacaoConsumoService()
    const { setAlert } = useUIService()
    const { navigateTo } = useNavigationService()
    const { produtoLimitacaoConsumoGetTipoConsumoAtivosApi } = useProdutoLimitacaoConsumoApi()

    const modo = createLimitacaoConsumoClienteForm.fields.ModoTravaCliente

    useEffect(() => {
        getProdutoLimitacaoConsumoList()
    }, [getProdutoLimitacaoConsumoList])

    useEffect(() => {
        if (currentUser?.IdUsuario && createLimitacaoConsumoClienteForm.fields.IdUsuario !== currentUser.IdUsuario) {
            setCreateLimitacaoConsumoClienteFormIdUsuario({
                IdUsuario: currentUser.IdUsuario
            })
        }
    }, [createLimitacaoConsumoClienteForm.fields.IdUsuario, currentUser?.IdUsuario, setCreateLimitacaoConsumoClienteFormIdUsuario])

    useEffect(() => {
        setIdProdutoParaTiposConsumo(0)
        setTiposDoProdutoSelecionado([])
        setSearchTermTipos("")
    }, [createLimitacaoConsumoClienteForm.fields.IdCliente])

    useEffect(() => {
        if (modo !== "tipoConsumo") {
            setIdProdutoParaTiposConsumo(0)
            setTiposDoProdutoSelecionado([])
            setSearchTermTipos("")
            setCreateLimitacaoConsumoClienteFormIdProduto({ IdProduto: 0 })
        }
    }, [modo, setCreateLimitacaoConsumoClienteFormIdProduto])

    useEffect(() => {
        if (modo !== "tipoConsumo" || idProdutoParaTiposConsumo <= 0) {
            setTiposDoProdutoSelecionado([])
            return
        }

        let cancelled = false
        setLoadingTipos(true)

        void (async () => {
            try {
                const list = await produtoLimitacaoConsumoGetTipoConsumoAtivosApi(idProdutoParaTiposConsumo)
                if (!cancelled) {
                    setTiposDoProdutoSelecionado(list)
                }
            } catch {
                if (!cancelled) {
                    setTiposDoProdutoSelecionado([])
                    setAlert({
                        message: "Não foi possível carregar as variáveis deste produto.",
                        type: "error"
                    })
                }
            } finally {
                if (!cancelled) {
                    setLoadingTipos(false)
                }
            }
        })()

        return () => {
            cancelled = true
        }
    }, [modo, idProdutoParaTiposConsumo, produtoLimitacaoConsumoGetTipoConsumoAtivosApi, setAlert])

    useEffect(() => {
        const buscarTravasAtivas = async () => {
            if (createLimitacaoConsumoClienteForm.fields.IdCliente && createLimitacaoConsumoClienteForm.fields.IdCliente > 0) {
                const travas = await findAllActiveLimitacaoConsumoCliente(createLimitacaoConsumoClienteForm.fields.IdCliente)
                setTravasAtivas(travas)
            }
        }
        buscarTravasAtivas()
    }, [createLimitacaoConsumoClienteForm.fields.IdCliente, findAllActiveLimitacaoConsumoCliente])

    useEffect(() => {
        if (!isFirstRender.current) {
            setLoading(false)
            isFirstRender.current = true

            if (!createLimitacaoConsumoClienteForm.fields.IdCliente || createLimitacaoConsumoClienteForm.fields.IdCliente === 0) {
                setAlert({
                    message: "Você deve selecionar um cliente",
                    type: "error"
                })

                navigateTo({
                    route: "/dashboard/trava/consumo"
                })
            }
        }
    }, [createLimitacaoConsumoClienteForm.fields, navigateTo, setAlert])

    const produtoTemTravaGeral = useMemo(() => {
        return (idProduto: number) =>
            travasAtivas.some((t) => t.IdProduto === idProduto && t.IdTipoConsumo == null)
    }, [travasAtivas])

    const tipoTemTravaAtiva = useMemo(() => {
        return (idTipoConsumo: number) =>
            travasAtivas.some((t) => t.IdTipoConsumo === idTipoConsumo)
    }, [travasAtivas])

    const alternarTipoConsumoSelecionado = useCallback((idTipoConsumo: number, checked: boolean) => {
        const atual = new Set(createLimitacaoConsumoClienteForm.fields.IdTipoConsumo)
        if (checked) {
            atual.add(idTipoConsumo)
        } else {
            atual.delete(idTipoConsumo)
        }
        setCreateLimitacaoConsumoClienteFormIdTipoConsumo({
            IdTipoConsumo: Array.from(atual)
        })
    }, [createLimitacaoConsumoClienteForm.fields.IdTipoConsumo, setCreateLimitacaoConsumoClienteFormIdTipoConsumo])

    const handleClose = () => {
        setCreateLimitacaoConsumoClienteOpenForm({
            open: false
        })
    }

    const produtoSelecionadoParaTipos = produtosList.find((p) => p.IdProduto === idProdutoParaTiposConsumo) ?? null

    return (
        loading ? <></>
            : <>
                <Dialog
                    open={true}
                    fullScreen
                    disableEscapeKeyDown
                    onClose={handleClose}
                    scroll={"paper"}
                    aria-labelledby="scroll-dialog-title"
                    aria-describedby="scroll-dialog-description"
                >
                    <AppBar sx={{ position: "relative" }}>
                        <Toolbar>
                            <Typography sx={{ ml: 2, flex: 1 }} textTransform={"uppercase"} fontWeight={"bold"} variant="h6" component="div">
                                Criar trava de consumo
                            </Typography>
                            <Button
                                color="inherit"
                                startIcon={<CloseIcon />}
                                onClick={handleClose}
                                aria-label="close"
                            >
                                Fechar
                            </Button>
                        </Toolbar>
                    </AppBar>
                    <DialogContent dividers={true}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    value={
                                        createLimitacaoConsumoClienteForm.fields.Quantidade === 0 ? ""
                                            : createLimitacaoConsumoClienteForm.fields.Quantidade.toLocaleString("pt-BR")
                                    }
                                    onChange={(e) => {
                                        setCreateLimitacaoConsumoClienteFormQuantidade({
                                            Quantidade: Number(onlyNumbers(e.target.value))
                                        })
                                    }}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    error={Boolean(createLimitacaoConsumoClienteForm.touched && createLimitacaoConsumoClienteForm.errors.find(item => item.key === "Quantidade"))}
                                    helperText={createLimitacaoConsumoClienteForm.errors.find(item => item.key === "Quantidade")?.message}
                                    name="quantidade"
                                    fullWidth
                                    id="quantidade"
                                    label="Quantidade"
                                    autoFocus
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold', color: 'text.primary', display: 'block' }}>
                                    Tipo de trava
                                </FormLabel>
                                <ToggleButtonGroup
                                    exclusive
                                    fullWidth
                                    value={modo}
                                    onChange={(_e, value) => {
                                        if (value != null) {
                                            setCreateLimitacaoConsumoClienteFormModoTravaCliente({
                                                ModoTravaCliente: value
                                            })
                                        }
                                    }}
                                >
                                    <ToggleButton value="produto" sx={{ py: 1.2, gap: 0.8, fontWeight: 'bold', fontSize: '0.82rem' }}>
                                        <CategoryIcon fontSize="small" /> Por produto
                                    </ToggleButton>
                                    <ToggleButton value="tipoConsumo" sx={{ py: 1.2, gap: 0.8, fontWeight: 'bold', fontSize: '0.82rem' }}>
                                        <ChecklistIcon fontSize="small" /> Por variável
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>
                            {modo === "produto" && (
                                <Grid item xs={12}>
                                    <Autocomplete
                                        fullWidth
                                        autoComplete
                                        includeInputInList
                                        options={[...produtosList].sort((a, b) =>
                                            a.Descricao.localeCompare(b.Descricao)
                                        )}
                                        getOptionLabel={(option) => option.Descricao.toUpperCase()}
                                        getOptionDisabled={(option) => produtoTemTravaGeral(option.IdProduto)}
                                        value={produtosList.find((produto) => produto.IdProduto === createLimitacaoConsumoClienteForm.fields.IdProduto) ?? null}
                                        onChange={(_event: unknown, newValue: IProdutoLimitacaoConsumoDetails | null) => {
                                            if (newValue) {
                                                if (produtoTemTravaGeral(newValue.IdProduto)) {
                                                    setAlert({
                                                        message: "Este produto já possui uma trava de consumo geral ativa para este cliente.",
                                                        type: "error"
                                                    })
                                                    return
                                                }
                                                setCreateLimitacaoConsumoClienteFormIdProduto({
                                                    IdProduto: newValue.IdProduto
                                                })
                                            } else {
                                                setCreateLimitacaoConsumoClienteFormIdProduto({
                                                    IdProduto: 0
                                                })
                                            }
                                        }}
                                        renderOption={(props, option) => {
                                            const temTravaGeral = produtoTemTravaGeral(option.IdProduto)
                                            return (
                                                <li {...props} style={{
                                                    ...props.style,
                                                    opacity: temTravaGeral ? 0.5 : 1,
                                                    cursor: temTravaGeral ? "not-allowed" : "pointer"
                                                }}>
                                                    {option.Descricao.toUpperCase()}
                                                    {temTravaGeral && " (Trava geral já ativa)"}
                                                </li>
                                            )
                                        }}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                label="Selecione um produto"
                                                error={Boolean(createLimitacaoConsumoClienteForm.touched && createLimitacaoConsumoClienteForm.errors.find(item => item.key === "IdProduto"))}
                                                helperText={
                                                    createLimitacaoConsumoClienteForm.errors.find(item => item.key === "IdProduto")?.message ||
                                                    (createLimitacaoConsumoClienteForm.fields.IdProduto > 0 && produtoTemTravaGeral(createLimitacaoConsumoClienteForm.fields.IdProduto)
                                                        ? "Este produto já possui uma trava geral ativa"
                                                        : "")
                                                }
                                            />
                                        }
                                    />
                                </Grid>
                            )}
                            {modo === "tipoConsumo" && (
                                <>
                                    <Grid item xs={12}>
                                        <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'action.hover', border: '1px solid', borderColor: 'divider', mb: 1 }}>
                                            <Typography variant="caption" fontWeight="bold" color="text.secondary" textTransform="uppercase" sx={{ display: 'block', mb: 1 }}>
                                                Formato da trava por variável
                                            </Typography>
                                            <ToggleButtonGroup
                                                exclusive
                                                fullWidth
                                                size="small"
                                                value={createLimitacaoConsumoClienteForm.fields.IsGrupo ? "grupo" : "individual"}
                                                onChange={(_e, value) => {
                                                    if (value != null) {
                                                        setCreateLimitacaoConsumoClienteFormIsGrupo({
                                                            IsGrupo: value === "grupo"
                                                        })
                                                    }
                                                }}
                                            >
                                                <ToggleButton value="individual" sx={{ py: 0.8, gap: 0.8, fontWeight: 'bold', fontSize: '0.78rem' }}>
                                                    <ChecklistIcon fontSize="small" /> Travas Individuais
                                                </ToggleButton>
                                                <ToggleButton value="grupo" sx={{ py: 0.8, gap: 0.8, fontWeight: 'bold', fontSize: '0.78rem' }}>
                                                    <LayersIcon fontSize="small" /> Trava em Grupo (Compartilhada)
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{ mb: 1 }}
                                        >
                                            <Typography variant="subtitle1" fontWeight="bold" color="primary" textTransform="uppercase">
                                                {createLimitacaoConsumoClienteForm.fields.IsGrupo ? "Variáveis do Grupo (Limite Compartilhado)" : "Variáveis (Travas Separadas)"}
                                            </Typography>
                                            <Chip
                                                icon={createLimitacaoConsumoClienteForm.fields.IsGrupo ? <LayersIcon style={{ fontSize: '0.9rem' }} /> : <ChecklistIcon style={{ fontSize: '0.9rem' }} />}
                                                label={createLimitacaoConsumoClienteForm.fields.IsGrupo ? "GRUPO COMPARTILHADO" : "TRAVAS SEPARADAS"}
                                                color={createLimitacaoConsumoClienteForm.fields.IsGrupo ? "primary" : "default"}
                                                size="small"
                                                sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                                            />
                                        </Stack>
                                        <Autocomplete
                                            fullWidth
                                            autoComplete
                                            includeInputInList
                                            options={[...produtosList].sort((a, b) =>
                                                a.Descricao.localeCompare(b.Descricao)
                                            )}
                                            getOptionLabel={(option) => option.Descricao.toUpperCase()}
                                            value={produtoSelecionadoParaTipos}
                                            onChange={(_event: unknown, newValue: IProdutoLimitacaoConsumoDetails | null) => {
                                                const id = newValue?.IdProduto ?? 0
                                                setIdProdutoParaTiposConsumo(id)
                                                setSearchTermTipos("")
                                                setCreateLimitacaoConsumoClienteFormIdProduto({ IdProduto: id })
                                                setCreateLimitacaoConsumoClienteFormIdTipoConsumo({ IdTipoConsumo: [] })
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Selecione o produto para listar as variáveis"
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        {Boolean(createLimitacaoConsumoClienteForm.touched && createLimitacaoConsumoClienteForm.errors.find(item => item.key === "IdTipoConsumo")) && (
                                            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                                                {createLimitacaoConsumoClienteForm.errors.find(item => item.key === "IdTipoConsumo")?.message}
                                            </Typography>
                                        )}
                                        {idProdutoParaTiposConsumo > 0 && loadingTipos && (
                                            <Typography variant="body2" color="text.secondary">Carregando variáveis...</Typography>
                                        )}
                                        {idProdutoParaTiposConsumo > 0 && !loadingTipos && tiposDoProdutoSelecionado.length === 0 && (
                                            <Typography variant="body2" color="text.secondary">Nenhuma variável disponível para este produto.</Typography>
                                        )}
                                        {idProdutoParaTiposConsumo > 0 && !loadingTipos && tiposDoProdutoSelecionado.length > 0 && (
                                            <FormControl component="fieldset" fullWidth>
                                                <FormLabel component="legend" sx={{ fontSize: "0.9rem", mb: 1 }}>
                                                    Escolha as variáveis
                                                </FormLabel>
                                                <TextField
                                                    size="small"
                                                    placeholder="Pesquisar variável..."
                                                    variant="outlined"
                                                    fullWidth
                                                    sx={{ mb: 1 }}
                                                    value={searchTermTipos}
                                                    onChange={(e) => setSearchTermTipos(e.target.value)}
                                                />
                                                <FormGroup sx={{ pl: 2 }}>
                                                    {tiposDoProdutoSelecionado
                                                        .filter((tc) =>
                                                            !searchTermTipos ||
                                                            tc.Descricao.toLowerCase().includes(searchTermTipos.toLowerCase())
                                                        )
                                                        .map((tipoConsumo) => {
                                                            const tipoBloqueado = tipoTemTravaAtiva(tipoConsumo.IdTipoConsumo)
                                                            return (
                                                                <FormControlLabel
                                                                    key={tipoConsumo.IdTipoConsumo}
                                                                    control={
                                                                        <Checkbox
                                                                            checked={createLimitacaoConsumoClienteForm.fields.IdTipoConsumo.includes(tipoConsumo.IdTipoConsumo)}
                                                                            disabled={tipoBloqueado}
                                                                            onChange={(event) => {
                                                                                alternarTipoConsumoSelecionado(tipoConsumo.IdTipoConsumo, event.target.checked)
                                                                            }}
                                                                        />
                                                                    }
                                                                    label={`${tipoConsumo.Descricao}${tipoBloqueado ? " (já possui trava ativa)" : ""}`}
                                                                />
                                                            )
                                                        })}
                                                </FormGroup>
                                            </FormControl>
                                        )}
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>Cancelar</Button>
                        <Button onClick={createLimitacaoConsumoCliente} variant="contained">Criar trava de consumo</Button>
                    </DialogActions>
                </Dialog>
            </>
    )
}
